import { createRouter, createWebHistory } from 'vue-router'
import Dashboard from '@/components/Dashboard.vue'
import SearchResults from '@/components/SearchResults.vue'
import LogIn from '@/components/LogIn.vue'
import Error403 from '@/components/Errors/403.vue'
import Settings from '@/components/Settings/index.vue'
import Roles, { RolesList, RolesEdit, RolesCreate } from '@/components/Roles'
import Users, { UsersList, UsersEdit, UsersCreate } from '@/components/Users'
import Employees, {
  EmployeesList,
  EmployeesArchivedList,
  EmployeesEdit,
  EmployeesCreate,
  EmployeesWeekOverview,
  EmployeesTimeoff,
} from '@/components/Employees'
import WorkOrders, {
  WorkOrdersList,
  WorkOrdersEdit,
  WorkOrdersCreate,
  WorkOrdersView,
} from '@/components/WorkOrders'
import Jobs, {
  JobsList,
  JobsEdit,
  JobsCreate,
  JobsView,
} from '@/components/Jobs'
import Branches, {
  BranchesList,
  BranchesEdit,
  BranchesCreate,
} from '@/components/Branches'
import Vehicles, {
  VehiclesList,
  VehiclesCreate,
  VehiclesEdit,
} from '@/components/Vehicles'
import VehicleTypes, {
  VehicleTypesList,
  VehicleTypesCreate,
  VehicleTypesEdit,
} from '@/components/VehicleTypes'
import WorkOrderCategories, {
  WorkOrderCategoriesList,
  WorkOrderCategoriesEdit,
} from '@/components/WorkOrderCategories'
import Clients, {
  ClientsList,
  ClientsArchivedList,
  ClientsEdit,
  ClientsCreate,
} from '@/components/Clients'
import Equipment, {
  EquipmentList,
  EquipmentCreate,
  EquipmentEdit,
} from '@/components/Equipment'
import EquipmentItems, {
  EquipmentItemsList,
  EquipmentItemsEdit,
  EquipmentItemsCreate,
  EquipmentItemView,
  EquipmentItemsPrint
} from '@/components/EquipmentItem'
import EquipmentItemLocations, {
  EquipmentItemLocationCreate,
} from '@/components/EquipmentItemLocations'
import RentalSuppliers, {
  RentalSuppliersList,
  RentalSuppliersEdit,
  RentalSuppliersCreate
} from '@/components/RentalSuppliers'
import Reports, {
  WorkInProgressReportsList,
  WorkInProgressReportsView
} from '@/components/Reports'
import { useAuth } from '@/utils/auth'

const routes = [
  {
    path: '/403',
    name: '403',
    component: Error403,
  },
  {
    path: '/',
    name: 'Dashboard',
    component: Dashboard,
    meta: {
      auth: true,
    },
  },
  {
    path: '/search-results',
    name: 'searchResults',
    component: SearchResults,
    meta: {
      auth: true,
    },
  },
  {
    path: '/login',
    name: 'LogIn',
    component: LogIn,
    meta: {
      auth: false,
    },
  },
  {
    path: '/settings',
    name: 'Settings',
    component: Settings,
    meta: {
      auth: true,
    },
  },
  {
    path: '/roles',
    name: 'Roles',
    component: Roles,
    redirect: { name: 'RolesList' },
    meta: {
      auth: {
        roles: ['view roles'],
      },
    },
    children: [
      {
        path: 'list',
        name: 'RolesList',
        component: RolesList,
      },
      {
        path: 'create',
        name: 'RolesCreate',
        component: RolesCreate,
        meta: {
          auth: {
            roles: ['create role'],
          },
        },
      },
      {
        path: 'edit/:id',
        name: 'RolesEdit',
        component: RolesEdit,
      },
    ],
  },
  {
    path: '/users',
    name: 'Users',
    component: Users,
    redirect: { name: 'UsersList' },
    meta: {
      auth: {
        roles: ['view users'],
      },
    },
    children: [
      {
        path: 'list',
        name: 'UsersList',
        component: UsersList,
      },
      {
        path: 'create',
        name: 'UsersCreate',
        component: UsersCreate,
        meta: {
          auth: {
            roles: ['create users'],
          },
        },
      },
      {
        path: 'edit/:id',
        name: 'UsersEdit',
        component: UsersEdit,
        meta: {
          auth: {
            roles: ['edit users'],
          },
        },
      },
    ],
  },
  {
    path: '/employees',
    name: 'Employees',
    component: Employees,
    redirect: { name: 'EmployeesList' },
    meta: {
      auth: {
        roles: ['view employees'],
      },
    },
    children: [
      {
        path: 'list',
        name: 'EmployeesList',
        component: EmployeesList,
      },
      {
        path: 'archived',
        name: 'EmployeesArchivedList',
        component: EmployeesArchivedList,
      },
      {
        path: 'create',
        name: 'EmployeesCreate',
        component: EmployeesCreate,
        meta: {
          auth: {
            roles: ['create employees'],
          },
        },
      },
      {
        path: 'edit/:id',
        name: 'EmployeesEdit',
        component: EmployeesEdit,
        meta: {
          auth: {
            roles: ['edit employees'],
          },
        },
      },
      {
        path: 'week-overview/:year?/:week?',
        name: 'EmployeesWeekOverview',
        component: EmployeesWeekOverview,
      },
      {
        path: 'timeoff/:employeeId?',
        name: 'EmployeesTimeoff',
        component: EmployeesTimeoff,
      },
    ],
  },
  {
    path: '/work-orders',
    name: 'WorkOrders',
    component: WorkOrders,
    meta: {
      auth: {
        roles: ['view work orders'],
      },
    },
    redirect: { name: 'WorkOrdersList' },
    children: [
      {
        path: 'list',
        name: 'WorkOrdersList',
        component: WorkOrdersList,
      },
      {
        path: 'create/:job_id?',
        name: 'WorkOrdersCreate',
        component: WorkOrdersCreate,
        meta: {
          auth: {
            roles: ['create work orders'],
          },
        },
      },
      {
        path: 'edit/:id',
        name: 'WorkOrdersEdit',
        component: WorkOrdersEdit,
        meta: {
          auth: {
            roles: ['edit work orders'],
          },
        },
      },
      {
        path: 'view/:id',
        name: 'WorkOrdersView',
        component: WorkOrdersView,
      },
    ],
  },
  {
    path: '/jobs',
    name: 'Jobs',
    component: Jobs,
    meta: {
      auth: {
        roles: [
          'view jobs',
          'view job equipment',
          'view job documents',
          'view job notes',
          'view job damage and loss reports',
          'view job print-outs',
          'view job additional costs',
          'view job cost overview',
          'view job financial documents',
        ],
      },
    },
    redirect: { name: 'JobsList' },
    children: [
      {
        path: 'list',
        name: 'JobsList',
        component: JobsList,
      },
      {
        path: 'create',
        name: 'JobsCreate',
        component: JobsCreate,
        meta: {
          auth: {
            roles: ['create jobs'],
          },
        },
      },
      {
        path: 'edit/:id',
        name: 'JobsEdit',
        component: JobsEdit,
        meta: {
          auth: {
            roles: ['edit jobs'],
          },
        },
      },
      {
        path: 'view/:id',
        name: 'JobsView',
        component: JobsView,
      },
    ],
  },
  {
    path: '/branches',
    name: 'Branches',
    component: Branches,
    meta: {
      auth: {
        roles: ['view branches'],
      },
    },
    redirect: { name: 'BranchesList' },
    children: [
      {
        path: 'list',
        name: 'BranchesList',
        component: BranchesList,
      },
      {
        path: 'create',
        name: 'BranchesCreate',
        component: BranchesCreate,
        meta: {
          auth: {
            roles: ['create branches'],
          },
        },
      },
      {
        path: 'edit/:id',
        name: 'BranchesEdit',
        component: BranchesEdit,
        meta: {
          auth: {
            roles: ['edit branches'],
          },
        },
      },
    ],
  },
  {
    path: '/vehicles',
    name: 'Vehicles',
    component: Vehicles,
    meta: {
      auth: {
        roles: ['view vehicles'],
      },
    },
    redirect: { name: 'VehiclesList' },
    children: [
      {
        path: 'list',
        name: 'VehiclesList',
        component: VehiclesList,
      },
      {
        path: 'create',
        name: 'VehiclesCreate',
        component: VehiclesCreate,
        meta: {
          auth: {
            roles: ['create vehicles'],
          },
        },
      },
      {
        path: 'edit/:id',
        name: 'VehiclesEdit',
        component: VehiclesEdit,
        meta: {
          auth: {
            roles: ['edit vehicles'],
          },
        },
      },
    ],
  },
  {
    path: '/work-order-categories',
    name: 'WorkOrderCategories',
    component: WorkOrderCategories,
    meta: {
      auth: {
        roles: ['view work order categories'],
      },
    },
    redirect: { name: 'WorkOrderCategoriesList' },
    children: [
      {
        path: 'list',
        name: 'WorkOrderCategoriesList',
        component: WorkOrderCategoriesList,
      },
      {
        path: 'edit/:id',
        name: 'WorkOrderCategoriesEdit',
        component: WorkOrderCategoriesEdit,
        meta: {
          auth: {
            roles: ['edit work order categories'],
          },
        },
      },
    ],
  },
  {
    path: '/vehicle-types',
    name: 'VehicleTypes',
    component: VehicleTypes,
    meta: {
      auth: {
        roles: ['view vehicles'],
      },
    },
    redirect: { name: 'VehicleTypesList' },
    children: [
      {
        path: 'list',
        name: 'VehicleTypesList',
        component: VehicleTypesList,
      },
      {
        path: 'create',
        name: 'VehicleTypesCreate',
        component: VehicleTypesCreate,
        meta: {
          auth: {
            roles: ['create vehicles'],
          },
        },
      },
      {
        path: 'edit/:id',
        name: 'VehicleTypesEdit',
        component: VehicleTypesEdit,
        meta: {
          auth: {
            roles: ['edit vehicles'],
          },
        },
      },
    ],
  },
  {
    path: '/clients',
    name: 'Clients',
    component: Clients,
    meta: {
      auth: {
        roles: ['view clients'],
      },
    },
    redirect: { name: 'ClientsList' },
    children: [
      {
        path: 'list',
        name: 'ClientsList',
        component: ClientsList,
      },
      {
        path: 'archived',
        name: 'ClientsArchivedList',
        component: ClientsArchivedList,
      },
      {
        path: 'create',
        name: 'ClientsCreate',
        component: ClientsCreate,
        meta: {
          auth: {
            roles: ['create clients'],
          },
        },
      },
      {
        path: 'edit/:id',
        name: 'ClientsEdit',
        component: ClientsEdit,
        meta: {
          auth: {
            roles: ['edit clients'],
          },
        },
      },
    ],
  },
  {
    path: '/equipment',
    name: 'Equipment',
    component: Equipment,
    meta: {
      auth: {
        roles: ['view equipment'],
      },
    },
    redirect: { name: 'EquipmentList' },
    children: [
      {
        path: 'list',
        name: 'EquipmentList',
        component: EquipmentList,
      },
      {
        path: 'create',
        name: 'EquipmentCreate',
        component: EquipmentCreate,
        meta: {
          auth: {
            roles: ['create equipment'],
          },
        },
      },
      {
        path: 'edit/:id',
        name: 'EquipmentEdit',
        component: EquipmentEdit,
        meta: {
          auth: {
            roles: ['edit equipment'],
          },
        },
      },
    ],
  },
  {
    path: '/equipment-items',
    name: 'EquipmentItems',
    component: EquipmentItems,
    meta: {
      auth: {
        roles: ['view inventory'],
      },
    },
    redirect: { name: 'EquipmentItemsList' },
    children: [
      {
        path: 'list',
        name: 'EquipmentItemsList',
        component: EquipmentItemsList,
      },
      {
        path: 'create',
        name: 'EquipmentItemsCreate',
        component: EquipmentItemsCreate,
        meta: {
          auth: {
            roles: ['create inventory'],
          },
        },
      },
      {
        path: 'edit/:id',
        name: 'EquipmentItemsEdit',
        component: EquipmentItemsEdit,
        meta: {
          auth: {
            roles: ['edit inventory'],
          },
        },
      },
      {
        path: 'view/:id',
        name: 'EquipmentItemView',
        component: EquipmentItemView,
      },
      {
        path: 'print',
        name: 'EquipmentItemsPrint',
        component: EquipmentItemsPrint,
      },
    ],
  },
  {
    path: '/equipment-item-locations',
    name: 'EquipmentItem items',
    component: EquipmentItemLocations,
    meta: {
      auth: {
        roles: ['view inventory'],
      },
    },
    redirect: { name: 'EquipmentItems' },
    children: [
      {
        path: 'create',
        name: 'EquipmentItemLocationCreate',
        component: EquipmentItemLocationCreate,
        meta: {
          auth: {
            roles: ['create inventory'],
          },
        },
      },
    ],
  },
  {
    path: '/rental-suppliers',
    name: 'RentalSuppliers',
    component: RentalSuppliers,
    meta: {
      auth: {
        roles: ['view rental suppliers'],
      },
    },
    redirect: { name: 'RentalSuppliersList' },
    children: [
      {
        path: 'list',
        name: 'RentalSuppliersList',
        component: RentalSuppliersList,
      },
      {
        path: 'create',
        name: 'RentalSuppliersCreate',
        component: RentalSuppliersCreate,
        meta: {
          auth: {
            roles: ['create rental suppliers'],
          },
        },
      },
      {
        path: 'edit/:id',
        name: 'RentalSuppliersEdit',
        component: RentalSuppliersEdit,
        meta: {
          auth: {
            roles: ['edit rental suppliers'],
          },
        },
      },
    ],
  },
  {
    path: '/reports',
    name: 'Reports',
    component: Reports,
    meta: {
      auth: {
        roles: ['view work in progress reports'],
      },
    },
    redirect: { name: 'WorkInProgressReportsList' },
    children: [
      {
        path: 'work-in-progress',
        name: 'WorkInProgressReportsList',
        component: WorkInProgressReportsList,
      },
      {
        path: 'work-in-progress/view/:id',
        name: 'WorkInProgressReportsView',
        component: WorkInProgressReportsView,
      },
    ],
  },
]

const router =  createRouter({
  history: createWebHistory(),
  routes,
})

let fetched = false

router.beforeEach(async(to) => {

  const auth = useAuth()

  if(!fetched) {
    try {
      await auth.fetch()
    } catch (e) {
      // skip
    }
    fetched = true
  }

  if (to.meta.auth === false) return true
  if (!auth.check()) return '/login'

  const roles = (to.meta.auth as { roles?: string[] })?.roles ?? []
  if (roles.length && !roles.some((role) => auth.check(role))) {
    return '/403'
  }

  return true
})

export default router
